import React, { useState, useEffect, memo } from 'react';
import { commentsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ThumbsUp, ThumbsDown, Flag, Edit2, Trash2, MessageCircle, Send } from 'lucide-react';
import StarRating from './StarRating';
import { ratingsAPI } from '../services/api';

// Move CommentItem outside to prevent recreation on every render - use memo to prevent infinite renders
const CommentItem = memo(({ 
  comment, 
  isReply, 
  user, 
  getReplies,
  editingId,
  setEditingId,
  editText,
  setEditText,
  handleEdit,
  handleDelete,
  handleLike,
  handleFlag,
  replyTo,
  setReplyTo,
  replyText,
  setReplyText,
  handleSubmitReply
}) => (
  <div className={`${isReply ? 'ml-12' : ''} mb-4`} data-testid={`comment-${comment.id}`}>
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
      {/* User info */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-electric flex items-center justify-center">
            <span className="text-background font-semibold">{comment.username.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <p className="font-semibold text-foreground">{comment.username}</p>
            <p className="text-xs text-foreground/60">{new Date(comment.created_at).toLocaleDateString()}</p>
          </div>
        </div>
        {user && user.id === comment.user_id && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setEditingId(comment.id);
                setEditText(comment.text);
              }}
              className="p-1 hover:bg-white/10 rounded"
            >
              <Edit2 className="h-4 w-4 text-foreground/60" />
            </button>
            <button
              onClick={() => handleDelete(comment.id)}
              className="p-1 hover:bg-white/10 rounded"
            >
              <Trash2 className="h-4 w-4 text-foreground/60" />
            </button>
          </div>
        )}
      </div>

      {/* Comment text */}
      {editingId === comment.id ? (
        <div className="mb-3">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric"
            rows="3"
          />
          <div className="flex space-x-2 mt-2">
            <button
              onClick={() => handleEdit(comment.id)}
              className="px-3 py-1 bg-electric text-background rounded-lg text-sm"
            >
              Save
            </button>
            <button
              onClick={() => setEditingId(null)}
              className="px-3 py-1 bg-white/10 text-foreground rounded-lg text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="text-foreground/90 mb-3">{comment.text}</p>
      )}

      {/* Actions */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => handleLike(comment.id, true)}
          className="flex items-center space-x-1 hover:text-electric transition-colors"
          disabled={!user}
        >
          <ThumbsUp className="h-4 w-4" />
          <span className="text-sm">{comment.likes || 0}</span>
        </button>
        <button
          onClick={() => handleLike(comment.id, false)}
          className="flex items-center space-x-1 hover:text-electric transition-colors"
          disabled={!user}
        >
          <ThumbsDown className="h-4 w-4" />
          <span className="text-sm">{comment.dislikes || 0}</span>
        </button>
        {!isReply && (
          <button
            onClick={() => setReplyTo(comment.id)}
            className="flex items-center space-x-1 hover:text-electric transition-colors"
            disabled={!user}
          >
            <MessageCircle className="h-4 w-4" />
            <span className="text-sm">Reply</span>
          </button>
        )}
        {user && user.id !== comment.user_id && (
          <button
            onClick={() => handleFlag(comment.id)}
            className="flex items-center space-x-1 hover:text-destructive transition-colors"
          >
            <Flag className="h-4 w-4" />
            <span className="text-sm">Flag</span>
          </button>
        )}
      </div>

      {/* Reply input */}
      {replyTo === comment.id && (
        <div className="mt-4 ml-12">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a reply..."
            className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric"
            rows="2"
          />
          <div className="flex space-x-2 mt-2">
            <button
              onClick={() => handleSubmitReply(comment.id)}
              className="px-3 py-1 bg-electric text-background rounded-lg text-sm"
            >
              Reply
            </button>
            <button
              onClick={() => {
                setReplyTo(null);
                setReplyText('');
              }}
              className="px-3 py-1 bg-white/10 text-foreground rounded-lg text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>

    {/* Replies */}
    {getReplies(comment.id).map(reply => (
      <CommentItem 
        key={reply.id} 
        comment={reply} 
        isReply={true}
        user={user}
        getReplies={getReplies}
        editingId={editingId}
        setEditingId={setEditingId}
        editText={editText}
        setEditText={setEditText}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        handleLike={handleLike}
        handleFlag={handleFlag}
        replyTo={replyTo}
        setReplyTo={setReplyTo}
        replyText={replyText}
        setReplyText={setReplyText}
        handleSubmitReply={handleSubmitReply}
      />
    ))}
  </div>
));

const CommentsSection = ({ contentId }) => {
  const { user, isAuthenticated } = useAuth();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [userRating, setUserRating] = useState(0);

  useEffect(() => {
    loadComments();
    if (isAuthenticated) {
      loadUserRating();
    }
  }, [contentId, sortBy]);

  const loadComments = async () => {
    try {
      const response = await commentsAPI.getAll(contentId, sortBy);
      setComments(response.data);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserRating = async () => {
    try {
      const response = await ratingsAPI.getUserRating(contentId);
      setUserRating(response.data.rating || 0);
    } catch (error) {
      console.error('Error loading rating:', error);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await commentsAPI.create({
        content_id: contentId,
        text: newComment,
        parent_comment_id: null
      });
      setNewComment('');
      loadComments();
    } catch (error) {
      if (error.response?.status === 403) {
        alert('You have been restricted from commenting due to community violations.');
      } else {
        console.error('Error posting comment:', error);
      }
    }
  };

  const handleSubmitReply = async (parentId) => {
    if (!replyText.trim()) return;

    try {
      await commentsAPI.create({
        content_id: contentId,
        text: replyText,
        parent_comment_id: parentId
      });
      setReplyTo(null);
      setReplyText('');
      loadComments();
    } catch (error) {
      console.error('Error posting reply:', error);
    }
  };

  const handleEdit = async (commentId) => {
    try {
      await commentsAPI.update(commentId, editText);
      setEditingId(null);
      setEditText('');
      loadComments();
    } catch (error) {
      console.error('Error editing comment:', error);
    }
  };

  const handleDelete = async (commentId) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        await commentsAPI.delete(commentId);
        loadComments();
      } catch (error) {
        console.error('Error deleting comment:', error);
      }
    }
  };

  const handleLike = async (commentId, isLike) => {
    try {
      await commentsAPI.like(commentId, isLike);
      loadComments();
    } catch (error) {
      console.error('Error liking comment:', error);
    }
  };

  const handleFlag = async (commentId) => {
    if (window.confirm('Flag this comment as inappropriate?')) {
      try {
        await commentsAPI.flag(commentId);
        alert('Comment has been flagged. Thank you for helping maintain our community.');
        loadComments();
      } catch (error) {
        console.error('Error flagging comment:', error);
      }
    }
  };

  const handleRate = async (rating) => {
    try {
      await ratingsAPI.submit(contentId, rating);
      setUserRating(rating);
      alert('Thank you for rating!');
    } catch (error) {
      console.error('Error submitting rating:', error);
    }
  };

  const getReplies = (parentId) => {
    return comments.filter(c => c.parent_comment_id === parentId);
  };

  const topLevelComments = comments.filter(c => !c.parent_comment_id);

  return (
    <div className="space-y-6" data-testid="comments-section">
      {/* Rating Section */}
      {isAuthenticated && (
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
          <h3 className="text-lg font-semibold text-foreground mb-3">Rate this content</h3>
          <StarRating rating={userRating} onRate={handleRate} size="lg" />
        </div>
      )}

      {/* Comments Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-foreground">Comments ({comments.length})</h3>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-electric"
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="most_liked">Most Liked</option>
        </select>
      </div>

      {/* New Comment Form */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmitComment} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4" style={{ backdropFilter: 'blur(20px) saturate(180%)' }}>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Share your thoughts..."
            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric text-foreground placeholder-foreground/40"
            rows="3"
            data-testid="comment-input"
          />
          <div className="flex justify-end mt-3">
            <button
              type="submit"
              disabled={!newComment.trim()}
              className="flex items-center space-x-2 px-6 py-2 bg-electric text-background rounded-lg font-medium hover:bg-electric/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-4 w-4" />
              <span>Post Comment</span>
            </button>
          </div>
        </form>
      ) : (
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 text-center">
          <p className="text-foreground/60">Please sign in to comment</p>
        </div>
      )}

      {/* Comments List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-4 border-electric/30 border-t-electric rounded-full animate-spin mx-auto"></div>
        </div>
      ) : topLevelComments.length > 0 ? (
        <div>
          {topLevelComments.map(comment => (
            <CommentItem 
              key={comment.id} 
              comment={comment}
              isReply={false}
              user={user}
              getReplies={getReplies}
              editingId={editingId}
              setEditingId={setEditingId}
              editText={editText}
              setEditText={setEditText}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              handleLike={handleLike}
              handleFlag={handleFlag}
              replyTo={replyTo}
              setReplyTo={setReplyTo}
              replyText={replyText}
              setReplyText={setReplyText}
              handleSubmitReply={handleSubmitReply}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl">
          <p className="text-foreground/60">No comments yet. Be the first to comment!</p>
        </div>
      )}
    </div>
  );
};

export default CommentsSection;
